import React, {FC} from 'react';

import download from 'assets/v3/guide/image/download.webp';
import engineConfig from 'assets/v3/guide/image/engine-config.webp';
import refine from 'assets/v3/guide/image/refine.webp';
import refineDone from 'assets/v3/guide/image/refine-done.webp';
import result from 'assets/v3/guide/image/result.webp';
import translating from 'assets/v3/guide/image/translating.webp';
import uploadEcommerce1 from 'assets/v3/guide/image/upload-ecommerce-1.webp';
import uploadEcommerce2 from 'assets/v3/guide/image/upload-ecommerce-2.webp';
import uploadLink from 'assets/v3/guide/image/upload-link.webp';
import uploadLocal from 'assets/v3/guide/image/upload-local.webp';

/** 사용 가이드「이미지 번역」탭 한국어 본문(zh.tsx와 요소 구조 1:1) — 2026-09-17 AI 번역 */
const KoContent: FC = () => {
  return (
    <>
      <h2>1. 업로드 전 설정</h2>
      <p>업로드하기 전에 필요에 따라 버전, 원본 언어, 대상 언어를 선택해 주세요.</p>
      <img alt="번역 설정 화면: 버전, 원본 언어, 대상 언어" decoding="async" loading="lazy" src={engineConfig} />
      <p>
        <strong>원본 언어</strong>: 원본 이미지에 들어 있는 텍스트의 언어를 선택해요.
      </p>
      <p>
        <strong>대상 언어</strong>: 번역하고 싶은 언어를 뜻해요.
      </p>

      <h3>번역 엔진은 어떻게 선택하면 될까요?</h3>
      <div className="v3-table-wrap">
        <table>
          <thead>
            <tr>
              <th>번역 엔진</th>
              <th>특징</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Alibaba Cloud / Baidu</td>
              <td>
                중국어 번역 결과가 더 자연스러워요. 외국어를 중국어로 번역할 때 가장 적합하고, 중국어를 외국어로 번역할
                때는 다소 약하지만 비용이 더 저렴해요.
              </td>
            </tr>
            <tr>
              <td>Google</td>
              <td>외국어 번역에 강하고, 지원 언어 범위가 넓어요.</td>
            </tr>
            <tr>
              <td>Papago</td>
              <td>한국 클라우드 업체 Naver에서 제공하는 엔진으로, 한국어로 번역할 때 가장 자연스러운 선택이에요.</td>
            </tr>
            <tr>
              <td>DeepL</td>
              <td>
                외국어 번역에 강하고 지원 범위가 넓어요(일본어, 영어, 독일어, 러시아어 등). 속어, 방언, 고전 표현도
                훌륭하게 처리해요.
              </td>
            </tr>
            <tr>
              <td>ChatGPT</td>
              <td>현재 가장 앞선 자연어 모델로, 정확하고 자연스러운 텍스트 번역을 제공해요.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. 이미지 업로드</h2>
      <h3>로컬 이미지 업로드</h3>
      <p>업로드 버튼을 클릭해 로컬 파일을 탐색하고, 필요한 상품 이미지를 선택해 주세요.</p>
      <img alt="로컬 파일 업로드 화면" decoding="async" loading="lazy" src={uploadLocal} />

      <h3>이미지 링크 입력</h3>
      <p>이미지 링크 탭으로 전환하면 번역할 이미지 URL을 한 번에 가져올 수 있어요.</p>
      <img alt="직접 링크 입력 화면" decoding="async" loading="lazy" src={uploadLink} />

      <h3>이커머스 상품 링크 붙여넣기</h3>
      <p>상품 링크를 붙여넣으면 해당 상품의 이미지가 자동으로 수집돼요. 그중 번역할 이미지를 선택해 주세요.</p>
      <img alt="이커머스 링크 붙여넣기 화면" decoding="async" loading="lazy" src={uploadEcommerce1} />
      <img alt="수집된 상품 목록 화면" decoding="async" loading="lazy" src={uploadEcommerce2} />

      <h2>3. 이미지 번역</h2>
      <p>업로드가 끝나면 【지금 번역】 버튼을 클릭하세요. AI가 자동으로 번역을 시작해요.</p>
      <img alt="번역 시작 화면" decoding="async" loading="lazy" src={translating} />
      <p>
        AI 이미지 번역에는 약 2~3초가 걸려요. 번역이 끝나면 원본 이미지와 번역 이미지를 나란히 비교해 보세요.
        만족스러우면 필요에 따라 【모두 다운로드】 또는 【번역 이미지 링크 복사】를 클릭하고, 만족스럽지 않거나
        레이아웃을 다시 잡고 싶다면 【보정】을 클릭해 보정 도구를 열고 이미지를 조정하세요.
      </p>
      <img alt="원본과 번역 결과 비교 화면" decoding="async" loading="lazy" src={result} />

      <h2>4. 이미지 보정</h2>
      <p>【보정】을 클릭해 보정 작업대를 연 뒤, 수정하고 싶은 텍스트를 클릭하세요.</p>
      <ul>
        <li>
          <strong>텍스트 편집 영역</strong>: 원본 텍스트를 수정하고 다시 번역할 수 있어요. 텍스트 스타일, 각도, 외곽선
          등을 조정할 수 있어요.
        </li>
        <li>
          <strong>복원 도구</strong>: 번역용 텍스트를 추가하거나, 스마트 지우개로 얼룩을 지우고 수정할 수 있어요.
        </li>
      </ul>
      <img alt="보정 작업대 화면" decoding="async" loading="lazy" src={refine} />
      <p>편집이 끝나면 【편집 완료】를 클릭해 변경 사항을 저장하고 보정 도구를 종료하세요.</p>
      <img alt="보정 완료 화면" decoding="async" loading="lazy" src={refineDone} />

      <h2>5. 다운로드</h2>
      <p>
        【모두 다운로드】 또는 【이미지 링크 복사】를 클릭하세요. 보정한 이미지는 다운로드하거나 내보낼 때 보정 전
        이미지를 자동으로 대체해요.
      </p>
      <img alt="번역 결과 다운로드 화면" decoding="async" loading="lazy" src={download} />
    </>
  );
};

export default KoContent;
